import axios from "../utils/AxiosInstance";
import { ResponseMessage } from "../models/ResponseMessage";
import { Constants } from "../constants/constants";
import { User } from "../models/User";
import { UserProfileResponse } from "../models/UserProfileResponse";
import {
  InviteUserRequest,
  UpdateUserOrganizationRequest,
  UserDetailsResponse,
  UserListResult,
} from "../models/UserModels";

const API_BASE_URL = Constants.API_BASE_URL;

export async function getUsersAsync(
  token: string,
  page: number,
  pageSize: number,
  organizationId?: string,
): Promise<ResponseMessage<UserListResult>> {
  const response = await axios.get(`${API_BASE_URL}/users`, {
    params: {
      page,
      pageSize,
      organizationId: organizationId || undefined,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function getUserAsync(
  token: string,
): Promise<ResponseMessage<UserProfileResponse>> {
  const response = await axios.get(`${API_BASE_URL}/users/details`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function getUserDetailsAsync(
  token: string,
  id: string,
): Promise<ResponseMessage<UserDetailsResponse>> {
  const response = await axios.get(`${API_BASE_URL}/users/${id}/details`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function inviteUserAsync(
  token: string,
  request: InviteUserRequest,
): Promise<ResponseMessage<User>> {
  const formData = new FormData();
  formData.append("email", request.email);
  formData.append("userName", request.userName ?? "");
  formData.append("contact", request.contact ?? "");
  formData.append("organizationId", request.organizationId);
  if (request.roleType != null) formData.append("roleType", String(request.roleType));
  if (request.roleName) formData.append("roleName", request.roleName);
  if (request.order != null) formData.append("order", String(request.order));
  if (request.designation) formData.append("designation", request.designation);
  if (request.qualification) formData.append("qualification", request.qualification);
  if (request.bio) formData.append("bio", request.bio);
  if (request.isPublic != null) {
    formData.append("isPublic", request.isPublic ? "true" : "false");
  }
  if (request.profilePictureFile) {
    formData.append("profilePictureFile", request.profilePictureFile);
  }

  const response = await axios.post(`${API_BASE_URL}/users/invite`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function updateUserOrganizationAsync(
  token: string,
  id: string,
  request: UpdateUserOrganizationRequest,
): Promise<ResponseMessage<unknown>> {
  const formData = new FormData();
  if (request.roleType != null) formData.append("roleType", String(request.roleType));
  if (request.roleName) formData.append("roleName", request.roleName);
  if (request.order != null) formData.append("order", String(request.order));
  if (request.designation) formData.append("designation", request.designation);
  if (request.qualification) formData.append("qualification", request.qualification);
  if (request.bio) formData.append("bio", request.bio);
  if (request.userName) formData.append("userName", request.userName);
  if (request.userId) formData.append("userId", request.userId);
  if (request.profilePictureUrl) {
    formData.append("profilePictureUrl", request.profilePictureUrl);
  }
  if (request.profilePictureFile) {
    formData.append("profilePictureFile", request.profilePictureFile);
  }
  if (request.isPublic != null) {
    formData.append("isPublic", request.isPublic ? "true" : "false");
  }

  const response = await axios.put(
    `${API_BASE_URL}/userorganizations/${id}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

export async function deleteUserAsync(
  token: string,
  id: string,
  organizationIds: string[],
): Promise<ResponseMessage<unknown>> {
  const response = await axios.delete(`${API_BASE_URL}/users/${id}`, {
    data: { organizationIds },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function acceptInvitationAsync(
  token: string,
  password: string,
  userName?: string,
  contact?: string,
): Promise<ResponseMessage<User>> {
  const response = await axios.post(`${API_BASE_URL}/users/accept-invitation`, {
    token,
    password,
    userName,
    contact,
  });

  return ResponseMessage.fromJson(response.data);
}